import React, { useState, useEffect } from 'react'
import {
  Form,
  Input,
  Button,
  Table,
  Space,
  Row,
  Col,
  Select,
  Modal,
} from 'antd'
import { EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons'
import AdminHeader from '../../CommonComponents/AdminHeader'
import AdminFooter from '../../CommonComponents/AdminFooter'
import {
  getMenu,
  getPage,
  savePage,
  updatePage,
} from '../../APIComponents/AdminApi'
import { toast } from 'react-toastify'
import axiosInstance from '../../../Utils/axiosInstance'

const PageMaster = () => {
  //============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'Page Master')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1

  //============================================

  const [form] = Form.useForm()
  const [pages, setPages] = useState([])
  const [menus, setMenus] = useState([])
  const [loading, setLoading] = useState(false)
  const [addLoading, setAddLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)

  const handleView = (record) => {
    setSelectedRecord(record)
    setViewModalVisible(true)
  }

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  })

  const fetchMenus = async () => {
    try {
      const res = await axiosInstance.get(getMenu)
      setMenus(res.data)
    } catch (err) {
      toast.error('Failed to fetch menus')
    }
  }

  // Fetch All Pages
  const fetchPages = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(getPage)
      setPages(res.data)
    } catch (err) {
      toast.error('Failed to fetch pages')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPages()
    fetchMenus()
  }, [])

  // Add or Update Page
  const onFinish = async (values) => {
    const { page, menu } = values

    if (!page.trim()) {
      toast.error('Please enter a valid page name')
      return
    }

    const objData = { page_name: page, menu_id: menu }

    try {
      if (editingId) {
        setUpdateLoading(true)
        const res = await axiosInstance.put(
          `${updatePage}/${editingId}`,
          objData,
        )
        if (res.data === 'UPDATED') {
          toast.success('Page updated successfully')
          fetchPages()
        }
      } else {
        setAddLoading(true)
        const res = await axiosInstance.post(savePage, objData)
        if (res.data === 'SAVED') {
          toast.success('Page added successfully')
          fetchPages()
        }
      }

      form.resetFields()
      setEditingId(null)
    } catch (err) {
      toast.error('Operation failed. Please try again.')
    } finally {
      setAddLoading(false)
      setUpdateLoading(false)
    }
  }

  // Edit Page
  const handleEdit = (record) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setEditingId(record.id)
    form.setFieldsValue({ page: record.page_name, menu: record.menu_id })
  }

  // Cancel Edit
  const handleCancel = () => {
    setEditingId(null)
    form.resetFields()
  }

  // Table Columns
  const columns = [
    {
      title: 'Sr. No',
      key: 'index',
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
      width: '10%',
    },
    {
      title: 'Menu Name',
      dataIndex: ['menu', 'menu_name'],
    },
    {
      title: 'Page Name',
      dataIndex: 'page_name',
      key: 'page_name',
    },
    ...(showActionColumn
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: '20%',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    title="View"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    icon={<EditOutlined />}
                    title="Edit"
                    onClick={() => handleEdit(record)}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <AdminHeader />

      <div
        className="student-form-section container-fluid"
        style={{ minHeight: '100vh' }}
      >
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-2">Page Master</h3>
          </div>

          <div className="row student-form-container shadow p-4 rounded bg-white">
            {/* Page Form */}
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              style={{ marginBottom: '20px' }}
            >
              <Row gutter={16} className="pt-3">
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Select Menu"
                    name="menu"
                    rules={[{ required: true, message: 'Please select menu' }]}
                  >
                    <Select placeholder="Select Menu">
                      {menus.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.menu_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Page Name"
                    name="page"
                    rules={[
                      { required: true, message: 'Please enter page name' },
                    ]}
                  >
                    <Input placeholder="Enter Page" />
                  </Form.Item>
                </Col>

                <Col
                  xs={24}
                  sm={12}
                  md={8}
                  style={{ display: 'flex', alignItems: 'end' }}
                >
                  <Form.Item>
                    <Space>
                      {!editingId ? (
                        <>
                          {pagePermission.is_new == 1 && (
                            <Button
                              type="primary"
                              htmlType="submit"
                              icon={<PlusOutlined />}
                              loading={addLoading}
                            >
                              Add
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <Button
                            type="primary"
                            htmlType="submit"
                            icon={<EditOutlined />}
                            loading={updateLoading}
                          >
                            Update
                          </Button>
                          <Button
                            onClick={handleCancel}
                            disabled={updateLoading}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {/* Page Table */}
            <Table
              columns={columns}
              dataSource={pages}
              rowKey="id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pages.length,
                onChange: (page, pageSize) => {
                  setPagination({
                    current: page,
                    pageSize: pageSize,
                  })
                },
              }}
              bordered
              loading={loading}
              className="shadow rounded custom-table p-0"
              style={{ overflowX: 'auto', border: '1px solid #dfdfdf' }}
            />
          </div>

          {/* ======== View Modal ====== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Page Details
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            centered
          >
            <div
              style={{
                padding: '15px',
                borderRadius: '8px',
                backgroundColor: '#f5f7fa',
                border: '1px solid #e6e6e6',
              }}
            >
              <Row gutter={[16, 16]}>
                {/* Menu Name */}
                <Col span={8}>
                  <span style={{ fontWeight: 600 }}>Menu Name :</span>
                </Col>

                <Col span={16}>
                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    {selectedRecord?.menu?.menu_name}
                  </div>
                </Col>

                {/* Page Name */}
                <Col span={8}>
                  <span style={{ fontWeight: 600 }}>Page Name :</span>
                </Col>

                <Col span={16}>
                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    {selectedRecord?.page_name}
                  </div>
                </Col>
              </Row>
            </div>
          </Modal>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default PageMaster
